import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { HandshakeIcon, MapPinIcon, PenLineIcon } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { AccordionItem } from "../components/Accordion";
import { ProductGrid } from "../components/ProductGrid";
import { SectionHeader } from "../components/SectionHeader";
import { PurchasePanel } from "../components/product/PurchasePanel";
import { products } from "../data/products";
import { shopifyImg } from "../utils/format";
import { btnPrimary, container, ease } from "../utils/styles";
export function ProductPage() {
  const {
    id
  } = useParams();
  const product = products.find(p => p.id === id);
  if (!product) {
    return <section className="flex flex-col items-center px-5 py-32 text-center" data-id="element-508" data-mp="9znddf57h9zg">
        <h1 className="text-3xl font-medium tracking-[-0.03em] text-ink" data-id="element-509" data-mp="1q9puy6pasc3v">Product niet gevonden</h1>
        <p className="mt-3 text-muted" data-id="element-510" data-mp="1lc7oqo2o0azk">Dit product bestaat niet (meer).</p>
        <Link to="/shop" className={`${btnPrimary} mt-8`} data-id="element-511" data-mp="1cndr88cbek5e">
          Naar de shop
        </Link>
      </section>;
  }
  const related = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);
  const isLamp = product.category === "Verlichting";
  const promises = [{
    icon: MapPinIcon,
    label: "Ontworpen en gemaakt in Nederland"
  }, {
    icon: PenLineIcon,
    label: product.personalizable ? "Speciaal voor jou geproduceerd" : "Zorgvuldig afgewerkt en gecontroleerd"
  }, {
    icon: HandshakeIcon,
    label: "Persoonlijk contact bij vragen"
  }];
  return <>
      <section className={`${container} pb-16 pt-6 lg:pb-24`} data-id="element-512" data-mp="12wrhuvv6ypv5">
        <Breadcrumbs items={[{
        label: "Home",
        to: "/"
      }, {
        label: product.category,
        to: `/shop?categorie=${product.category}`
      }, {
        label: product.name
      }]} data-id="element-513" data-mp="17pkib7jmnc1f" />
        <div className="mt-6 grid gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-514" data-mp="f7gnpx8cjtpz">
          <div className="lg:col-span-7" data-id="element-515" data-mp="77k241cu2l15">
            <motion.div key={product.id} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.25,
            ease
          }} className="overflow-hidden rounded-xl bg-sand lg:sticky lg:top-[96px]" data-id="element-516" data-mp="76iopq8chmg9">
              <img src={shopifyImg(product.image, 1800)} alt={product.imageAlt} className="aspect-square w-full object-cover" data-id="element-517" data-mp="c03fax7lze5f" />
            </motion.div>
          </div>

          <div className="lg:col-span-5" data-id="element-518" data-mp="3gdieiwdx5xxb">
            <PurchasePanel product={product} data-id="element-519" data-mp="3gel5t5f5m2dr" />

            <ul className="mt-8 space-y-3 rounded-xl bg-sand p-5" data-id="element-520" data-mp="3uci5wbstgqx6">
              {promises.map(({
              icon: Icon,
              label
            }) => <li key={label} className="flex items-center gap-3 text-sm text-ink/80" data-id="element-521" data-mp="3pcnb3x5x7h6u">
                  <Icon className="h-4 w-4 shrink-0 text-gold-deep" strokeWidth={1.8} data-id="element-522" data-mp="19lrcvlfsw2ij" />
                  {label}
                </li>)}
            </ul>

            <div className="mt-6 border-t border-line" data-id="element-523" data-mp="2ml2nws5vl9o3">
              <AccordionItem title="Specificaties" defaultOpen data-id="element-524" data-mp="2mjj7n1k7yymb">
                <dl className="space-y-2.5" data-id="element-525" data-mp="2rkp3h896f202">
                  {product.specs.map(s => <div key={s.label} className="grid grid-cols-[120px_1fr] gap-4 text-sm" data-id="element-526" data-mp="2dkxrte64txp8">
                      <dt className="text-muted" data-id="element-527" data-mp="2ij7mlnny2wab">{s.label}</dt>
                      <dd className="text-ink" data-id="element-528" data-mp="2illh9vepq6hc">{s.value}</dd>
                    </div>)}
                </dl>
              </AccordionItem>
              {isLamp && <AccordionItem title="Lichtadvies" data-id="element-529" data-mp="3aa5i0zhp4uzh">
                  Gebruik een energiezuinige LED-lamp met warm-wit licht tussen 2200K en 2700K. Dat benadrukt het reliëf en geeft een rustige sfeer.
                </AccordionItem>}
              <AccordionItem title="Productie & afwerking" data-id="element-530" data-mp="3p87j931994dk">
                Gemaakt door Koers Design in Nederland. Kleine laaglijnen horen bij het 3D-printproces en maken ieder exemplaar uniek.
              </AccordionItem>
              <AccordionItem title="Andere kleur, maat of tekst?" data-id="element-531" data-mp="25dwcze7eswg2">
                Veel ontwerpen kunnen we aanpassen.{" "}
                <Link to="/#maatwerk" className="font-medium text-ink underline underline-offset-4" data-id="element-532" data-mp="20c72q0s0gmt1">
                  Stuur ons je wens
                </Link>
                .
              </AccordionItem>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && <section aria-labelledby="related-heading" className="border-t border-line bg-paper py-16 lg:py-24" data-id="element-533" data-mp="2o2t4xbfc3cv4">
          <div className={container} data-id="element-534" data-mp="2k3u70as0sgd6">
            <SectionHeader id="related-heading" title="Misschien ook mooi" link={{
          label: `Alle ${product.category.toLowerCase()}`,
          to: `/shop?categorie=${product.category}`
        }} data-id="element-535" data-mp="2fahj62v60dlh" />
            <div className="mt-10" data-id="element-536" data-mp="37rbqzk51ma5l">
              <ProductGrid products={related} data-id="element-537" data-mp="37rk7rn4adzm2" />
            </div>
          </div>
        </section>}
    </>;
}